import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { map, catchError, tap, finalize, shareReplay } from 'rxjs/operators';
import { AuthService } from 'src/app/service/auth.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import { QueryParams } from 'src/app/shared/models/query-params.interface';
import { ScoreRequestTypes } from 'src/app/shared/models/score-time-series.interface';
import { SCANNER_REFRESH_PARAMS } from './scanner.constants';

export interface ScoreDataPoint {
  date: string;
  score: number;
}

export interface ScannerScoreState {
  scoreData: ScoreDataPoint[];
  scoreSentiLongData: ScoreDataPoint[];
  scoreSentiShortData: ScoreDataPoint[];
  scoreEData: ScoreDataPoint[];
  scoreSData: ScoreDataPoint[];
  scoreGData: ScoreDataPoint[];
}

const EMPTY_STATE: ScannerScoreState = {
  scoreData: [],
  scoreSentiLongData: [],
  scoreSentiShortData: [],
  scoreEData: [],
  scoreSData: [],
  scoreGData: [],
};

/**
 * Shared service for scanner score data.
 * Uses BehaviorSubject pattern to provide reactive state management.
 */
@Injectable({
  providedIn: 'root',
})
export class ScannerScoreStateService {
  private readonly scoreStateSubject =
    new BehaviorSubject<ScannerScoreState | null>(null);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);

  /** Observable of the current score state */
  readonly scoreState$ = this.scoreStateSubject.asObservable();

  /** Observable of loading state */
  readonly loading$ = this.loadingSubject.asObservable();

  /** Observable of error state */
  readonly error$ = this.errorSubject.asObservable();

  private pendingRequest$: Observable<ScannerScoreState> | null = null;
  private pendingParamsKey: string | null = null;
  private lastCompletedParamsKey: string | null = null;

  constructor(
    private auth: AuthService,
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
  ) {}

  /**
   * Fetches score data and updates the reactive state.
   */
  fetchScoreData(params: QueryParams): Observable<ScannerScoreState> {
    const paramsKey = this.getParamsKey(params);

    // Only reuse pending request if params haven't changed
    if (this.pendingRequest$ && this.pendingParamsKey === paramsKey) {
      return this.pendingRequest$;
    }

    // Return cached result if same params already completed
    if (
      this.lastCompletedParamsKey === paramsKey &&
      this.scoreStateSubject.value
    ) {
      return of(this.scoreStateSubject.value);
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);
    this.pendingParamsKey = paramsKey;

    this.pendingRequest$ = this.loadScoreData(params).pipe(
      tap((state) => {
        this.scoreStateSubject.next(state);
        this.lastCompletedParamsKey = paramsKey;
      }),
      catchError((error) => {
        const errorMessage = error?.message || 'Failed to load score data';
        this.errorSubject.next(errorMessage);
        const emptyState = { ...EMPTY_STATE };
        this.scoreStateSubject.next(emptyState);
        return of(emptyState);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
        this.pendingRequest$ = null;
        this.pendingParamsKey = null;
      }),
      shareReplay(1),
    );

    return this.pendingRequest$;
  }

  private getParamsKey(params: QueryParams): string {
    const relevantParams: Record<string, unknown> = {};
    for (const key of SCANNER_REFRESH_PARAMS) {
      if (params[key] !== undefined) {
        relevantParams[key] = params[key];
      }
    }
    return JSON.stringify(relevantParams);
  }

  /**
   * Clears the current state
   */
  clearState(): void {
    this.scoreStateSubject.next(null);
    this.loadingSubject.next(false);
    this.errorSubject.next(null);
    this.pendingRequest$ = null;
    this.lastCompletedParamsKey = null;
  }

  private loadScoreData(params: QueryParams): Observable<ScannerScoreState> {
    const defaultScoreId = this.auth.scorelabSettings.defaultScore;
    const resolvedScore = this.scoreUtils.resolveScoreType(defaultScoreId);

    const isCustomScore =
      typeof resolvedScore === 'object' && 'event_weights' in resolvedScore;
    const isEsg = defaultScoreId === 'esg';
    const isSentiment = defaultScoreId === 'sentiment';

    const timeframe = params.time || '7';
    const timeframeLength = DateUtils.chartFromTo(timeframe)?.period;
    const shortPeriod = timeframeLength <= 7 ? 1 : 7;
    const longPeriod = timeframeLength <= 7 ? 7 : 30;

    const requests: { [key: string]: Observable<ScoreDataPoint[]> } = {};

    if (!isSentiment) {
      requests['score'] = this.fetchScoreTimeSeries(resolvedScore);
    }

    if (isSentiment) {
      requests['sentimentShort'] = this.fetchScoreTimeSeries({
        period: shortPeriod,
      });
      requests['sentimentLong'] = this.fetchScoreTimeSeries({
        period: longPeriod,
      });
    }

    if (isEsg && !isCustomScore) {
      requests['scoreE'] = this.fetchScoreTimeSeries('e_standard');
      requests['scoreS'] = this.fetchScoreTimeSeries('s_standard');
      requests['scoreG'] = this.fetchScoreTimeSeries('g_standard');
    }

    if (Object.keys(requests).length === 0) {
      return of({ ...EMPTY_STATE });
    }

    return forkJoin(requests).pipe(
      map((results) => ({
        scoreData: results['score'] || [],
        scoreSentiLongData: results['sentimentLong'] || [],
        scoreSentiShortData: results['sentimentShort'] || [],
        scoreEData: results['scoreE'] || [],
        scoreSData: results['scoreS'] || [],
        scoreGData: results['scoreG'] || [],
      })),
      catchError(() => of({ ...EMPTY_STATE })),
    );
  }

  private fetchScoreTimeSeries(
    scoreType: ScoreRequestTypes,
  ): Observable<ScoreDataPoint[]> {
    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {
          collapse_on: ['participant'],
        },
        body: {
          score_type: scoreType,
        },
      })
      .pipe(
        map((response) => response.score_ts?.[0]?.score_ts || []),
        catchError(() => of([])),
      );
  }
}
